import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideRouter, RouterModule, withComponentInputBinding, withDebugTracing } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, IPublicClientApplication, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { CacheControlInterceptor } from '@aifs-shared/net/cache-control.interceptor';
import { JWT_OPTIONS, JwtHelperService, JwtModule } from '@auth0/angular-jwt';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { AuthenticationService } from '@aifs-shared/auth/authentication.service';
import { UserService } from '@aifs-shared/user/user.service';
import { GlobalErrorHandler } from '@aifs-shared/errors/error-handler';
import { HttpErrorInterceptor } from '@aifs-shared/errors/http-error-handler';
import { DefaultRequestOptions } from '@aifs-shared/net/default-request-options.service';
import { MessageService } from '@aifs-shared/message/message.service';
import { VisaService } from './applicant/application/visa/visa.service';
import { ToDoService } from '@aifs-shared/todo/todo.service';
import { PoliceCheckService } from '@aifs-shared/police-check/police-check.service';
import { UploadService } from '@aifs-shared/upload/upload.service';
import { MenuService } from '@ui/cf-menu/menu.service';


export function loggerCallback(logLevel: LogLevel, message: string) {
    //console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.authClientId,
            authority: environment.authority,
            redirectUri: '/',
            postLogoutRedirectUri: '/'
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage
        },
        system: {
            allowNativeBroker: false, // Disables WAM Broker
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Info,
                piiLoggingEnabled: false
            }
        }
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(environment.protectedResourceMap, environment.authScopes);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.authScopes]
        },
        loginFailedRoute: '/login-failed'
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes, withComponentInputBinding(), withDebugTracing()),
        importProvidersFrom(BrowserModule),
        importProvidersFrom(NgbModule),
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheControlInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DefaultRequestOptions,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        {
            provide: JWT_OPTIONS,
            useClass: JwtModule
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        JwtHelperService,
        AuthenticationService,
        MessageService,
        MenuService,
        PoliceCheckService,
        RouterModule,
        ToDoService,
        UploadService,
        UserService,
        VisaService,
    ]
};